import React from 'react';
import PropTypes from 'prop-types';
import { post, get } from 'axios';

import { contentSvgs, callbackSvgs } from 'assets/icons';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import history from 'utils/history';
import { formatDate, dayOfWeek } from 'utils/moment';
import { FormatCurrency } from 'utils/core';
import { getStatus, ExportPDF } from './utils';

import * as S from './styles';

const { refundFiles, refundsCategoryTypes } = endpoints;

function RefundsTable({
  userID,
  data,
  setOverlayVisibility,
  setOverlayType,
  setOverlayAction,
  setIdToRemove,
  deleteEndPoint,
  renderToast,
  setRealoadData,
}) {
  const [refundsCategories] = useFetch(refundsCategoryTypes);
  const HandleExportDocs = async id => {
    const response = await get(`${refundFiles}/${id}`);
    const { data: resData } = await response;
    if (resData) {
      const { input } = await resData;
      return ExportPDF(input);
    }
  };
  const HandleRemoveREfund = id => {
    if (id) {
      const sendToDelete = { id_usr_up: userID, id_reembolso: id };

      post(deleteEndPoint, sendToDelete)
        .then(() =>
          renderToast(
            'success',
            'Solicitação de reembolso removida com sucesso',
            6000,
          ),
        )
        .then(() => setRealoadData(prev => !prev))
        .catch(() => {
          renderToast(
            'error',
            'Erro ao exluir o reembolso. Por favor, tente novamente',
            6000,
          );
        });

      return;
    }

    return renderToast(
      'error',
      'Erro durante a requisição. Por favor, tente novamente',
      6000,
    );
  };
  const HandleEditRefund = stateData => {
    history.push({
      pathname: '/auth/appointments/refunds-history/edit-refunds',
      state: { ...stateData, viewType: 'edit' },
    });
  };

  const showMoreInfo = (itens, valor) => {
    const total = itens.map(i => ({ total: i.quantidade * i.valor_unitario }));
    return (
      <S.MoreActionsIncoContainer>
        {FormatCurrency(valor)}
        {!!itens.length && (
          <S.ProjectMore>
            <S.MoreActionsIcon>{contentSvgs('information')}</S.MoreActionsIcon>
            <S.MoreActions>
              <S.MoreActionsHeader>Mais informações</S.MoreActionsHeader>
              {refundsCategories && (
                <S.MoreActionsTable>
                  <S.MoreActionsTableHeader>
                    <div>Categoria</div>
                    <div>Qtd.</div>
                    <div>Valor Unit.</div>
                    <div>Total</div>
                  </S.MoreActionsTableHeader>
                  {itens.map((item, k) => {
                    const category = refundsCategories.find(
                      c => c.value === item.id_tipo_reembolso,
                    );
                    return (
                      category && (
                        <S.MoreActionsInfo key={item.id_item_reembolso}>
                          <S.InfoStrong>{category.label}</S.InfoStrong>
                          <S.InfoSpan>{item.quantidade}</S.InfoSpan>
                          <S.InfoSpan>
                            {FormatCurrency(item.valor_unitario)}
                          </S.InfoSpan>
                          <S.InfoSpan>
                            {FormatCurrency(total[k].total)}
                          </S.InfoSpan>
                        </S.MoreActionsInfo>
                      )
                    );
                  })}
                </S.MoreActionsTable>
              )}
            </S.MoreActions>
          </S.ProjectMore>
        )}
      </S.MoreActionsIncoContainer>
    );
  };

  return (
    <S.Table>
      <div className="table-responsive">
        {!data.length ? (
          <S.Callback>
            {callbackSvgs('circularMan')}
            <S.CallbackText>
              Não existem solicitações de reembolso neste mês
            </S.CallbackText>
          </S.Callback>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Projeto</th>
                <th scope="col">Valor reembolso</th>
                <th scope="col">Status</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td>
                    {formatDate(row.dt_solicitacao.split('-').join(','))}
                    <span>
                      {' '}
                      {`(${dayOfWeek(
                        row.dt_solicitacao.split('-').join(','),
                      )})`}
                    </span>
                  </td>
                  <td>{`${row.nome_projeto} | ${row.idetificacao_projeto}`}</td>
                  <td>{showMoreInfo(row.itens, row.valor_total)}</td>
                  <S.TableCol
                    className={`status-${getStatus(
                      row.id_aprovacao_gestor,
                      'status',
                    )}`}
                    Text={row.negativa}
                  >
                    {getStatus(row.id_aprovacao_gestor, 'text')}
                  </S.TableCol>
                  <td>
                    <S.ProjectMore>
                      {contentSvgs('moreInfos')}
                      <S.MoreActions>
                        <S.MoreActionsHeader>Ações</S.MoreActionsHeader>
                        <S.MoreActionsItem
                          text="Editar reembolso"
                          handleClick={() => HandleEditRefund(row)}
                          disabled={row.id_aprovacao_gestor > 1}
                        />
                        <S.MoreActionsItem
                          text="Visualizar anexos"
                          handleClick={() => HandleExportDocs(row.id_reembolso)}
                        />
                        <S.MoreActionsItem
                          text="Excluir reembolso"
                          Type="danger"
                          disabled={row.id_aprovacao_gestor > 1}
                          handleClick={() => {
                            setOverlayVisibility(true);
                            setOverlayType('delete');
                            setIdToRemove(row.id_reembolso);
                            setOverlayAction(() => HandleRemoveREfund);
                          }}
                        />
                      </S.MoreActions>
                    </S.ProjectMore>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </S.Table>
  );
}

RefundsTable.defaultProps = {
  data: [],
};

RefundsTable.propTypes = {
  data: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  setOverlayVisibility: PropTypes.func.isRequired,
  setOverlayType: PropTypes.func.isRequired,
  setOverlayAction: PropTypes.func.isRequired,
  setIdToRemove: PropTypes.func.isRequired,
  userID: PropTypes.string.isRequired,
  deleteEndPoint: PropTypes.string.isRequired,
  renderToast: PropTypes.func.isRequired,
  setRealoadData: PropTypes.func.isRequired,
};

export default RefundsTable;
